import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppDispatch, AppGetState } from '@/store'
import Api from '@/services/http'
import { queryDictionary, fetchDictionaryByKey, fetchProviderList } from '@/services/api/dictionary'
import * as Types from './type'
import { createSetReducers } from '@/store/storeUtils'

const initialState: Types.DicsType = {
  providerDic: {},
  departureDics: [],
  arrivalDics: [],
  countryMapDics: {},
  countryDics: [],
  currencyDics: [],
  filterSenderCountries: [],
  filterArrivalCountries: [],
  senderCountryInfo: {} as Types.DicsType['senderCountryInfo'],
  senderCountryProviderList: [],
  dictionaryMap: {},
}

export const dictinarySlice = createSlice({
  name: 'dictinary',
  initialState,
  reducers: {
    ...createSetReducers(initialState),
    setCountryDics(state, action: PayloadAction<Types.DicsType['departureDics']>) {
      const departureList = action.payload.filter(({ isDeparture }) => !!isDeparture) || []
      const arrivalList = action.payload.filter(({ isArrival }) => !!isArrival) || []
      const countryDics = [...arrivalList, ...departureList]
      // const countryDics = action.payload
      const countryMapDics = {}
      countryDics.forEach((country) => {
        countryMapDics[country.countryCode] = country
      })
      return {
        ...state,
        departureDics: departureList,
        arrivalDics: arrivalList,
        countryDics,
        countryMapDics,
      }
    },
    setDictionaryMap(state, action) {
      return {
        ...state,
        dictionaryMap: {
          ...state.dictionaryMap,
          [action.payload.key]: action.payload.value,
        },
      }
    },
  },
})
const {
  setCountryDics,
  setProviderDic,
  setCurrencyDics,
  setFilterSenderCountries,
  setFilterArrivalCountries,
  setSenderCountryInfo,
  setSenderCountryProviderList,
  setDictionaryMap,
} = dictinarySlice.actions

/** 查询服务商logo配置 */
export const queryProviderLogo = () => async (dispatch: AppDispatch) => {
  const [dicRes, userRes] = await Promise.all([queryDictionary(), fetchProviderList()])
  const { bizCode, data } = dicRes
  if (bizCode === 'SUCCESS') {
    const { providerLogoRespVos } = data || {}
    if (providerLogoRespVos) {
      const companyList = providerLogoRespVos.map(({ providerCode, logoUrl, providerName }) => ({
        name: providerCode,
        url: logoUrl,
        label: providerName,
      }))

      const formatCompanyList = companyList.reduce((obj, item) => {
        const key = item.name
        if (key) obj[key] = item
        return obj
      }, {})
      dispatch(setProviderDic(formatCompanyList))
    }
  }
  if (userRes.bizCode === 'SUCCESS') {
    const { providerLogoRespVos } = userRes.data || {}
    if (providerLogoRespVos) {
      const companyList = providerLogoRespVos.map(({ providerCode, logoUrl, providerName }) => ({
        name: providerCode,
        url: logoUrl,
        label: providerName,
      }))
      dispatch(setSenderCountryProviderList(companyList))
    }
  }
}

export const fetchDictionary = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  const res = await fetchDictionaryByKey('COUNTRY_LIST')
  const { auth } = getState()
  const countryCode = auth?.userInfo?.selfArea
  const { dictionaryInfos } = res?.data || {}

  if (dictionaryInfos) {
    const list = dictionaryInfos.map(({ value }) => JSON.parse(value))
    const currencyLsit = list?.map((item) => item.currency) || []
    const senderCountryInfo = list?.find((item) => item?.countryCode === countryCode)
    dispatch(setCountryDics(list?.sort((a, b) => (a.countryName > b.countryName ? 1 : -1))))
    dispatch(setCurrencyDics(Array.from(new Set(currencyLsit))))
    /** 用户所属国家对应的配置信息 */
    senderCountryInfo && dispatch(setSenderCountryInfo(senderCountryInfo))
    return list
  }

  return Promise.reject(res)
}

/**
 * 获取用户注册国家支持的收货国家
 */
export const fetchFilterCountry = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  const res = await Api.admin.post<Types.ResCountry>('/api/express/auth_country', {
    filterByUserCountry: true,
  })

  if (res?.bizCode === 'SUCCESS') {
    const { dictionary } = getState()
    const filterSenderCountries = res?.data?.senderCountryList?.map((item) => dictionary.countryMapDics?.[item?.countryCode]).filter((item) => !!item)
    const filterArrivalCountries = res?.data?.consigneeCountryList?.map((item) => dictionary.countryMapDics?.[item?.countryCode]).filter((item) => !!item)

    // 根据国家countryName A-Z排序
    if (filterSenderCountries && filterSenderCountries.length) {
      filterSenderCountries.sort((a, b) => (a.countryName > b.countryName ? 1 : -1))
    }

    if (filterArrivalCountries && filterArrivalCountries.length) {
      filterArrivalCountries.sort((a, b) => (a.countryName > b.countryName ? 1 : -1))
    }

    dispatch(setFilterSenderCountries(filterSenderCountries))
    dispatch(setFilterArrivalCountries(filterArrivalCountries))

    return Promise.resolve(filterArrivalCountries)
  }

  return Promise.reject(res)
}

// 加载指定字典
export const fetchAndStoreDictionaryByKey = (key) => async (dispatch: AppDispatch) => {
  const res = await fetchDictionaryByKey(key)
  if (res?.data?.dictionaryInfos?.length > 0) {
    dispatch(
      setDictionaryMap({
        key,
        value: res?.data?.dictionaryInfos,
      })
    )
  }
}

export default dictinarySlice.reducer
